/**
 * ReferralForm — view component for the referral-intake workflow.
 *
 * Rendered on the canvas when the agent calls show_view("referral_form").
 * Pure React component — receives {values, currentField, lastFilled,
 * schema_id} in its data prop, renders a fully-editable HTML form,
 * emits actions up to the workspace session when the user types.
 *
 * UX principles:
 *   - All fields are always editable (keyboard-first).
 *   - Voice-filled fields flash green for 1.5s (the `lastFilled` prop
 *     names the most recently filled field).
 *   - Current field (what the voice agent is asking about) gets a
 *     subtle highlight + scrolls into view.
 *   - Required-but-empty shows a red asterisk; type/format issues
 *     show inline (light, non-blocking).
 *   - Submit button is enabled once all required fields have values.
 */

import React, { useEffect, useRef, useState } from "react";
import { SCHEMAS_BY_ID, allFields, countFilled } from "../schemas/referral-v1.js";

export default function ReferralForm({ data = {}, onAction }) {
  const schema = SCHEMAS_BY_ID[data.schema_id] || SCHEMAS_BY_ID["referral-v1"];
  const values = data.values || {};
  const currentField = data.currentField || null;
  const lastFilled = data.lastFilled || null;

  const { filled, required } = countFilled(values, schema);
  const canSubmit = filled === required;

  // Scroll the current field into view when it changes
  const currentRef = useRef(null);
  useEffect(() => {
    if (currentField && currentRef.current) {
      currentRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [currentField]);

  // Track which field was "just filled" so we can flash it green briefly.
  const [flashField, setFlashField] = useState(null);
  useEffect(() => {
    if (!lastFilled) return;
    setFlashField(lastFilled);
    const t = setTimeout(() => setFlashField(null), 1800);
    return () => clearTimeout(t);
  }, [lastFilled]);

  const emit = (fieldId, value) => {
    if (!onAction) return;
    onAction({
      type: "field_changed",
      field_id: fieldId,
      value,
    });
  };

  const fieldStyle = (f) => {
    const active = currentField === f.id;
    const flash = flashField === f.id;
    const empty = f.required && (values[f.id] === undefined || values[f.id] === null || values[f.id] === "" ||
      (Array.isArray(values[f.id]) && values[f.id].length === 0));
    return {
      transition: "all 0.5s ease",
      ...(flash ? { boxShadow: "0 0 0 3px #81c784, 0 0 12px rgba(76,175,80,0.3)", background: "#f1f8e9" } : {}),
      ...(active && !flash ? { boxShadow: "0 0 0 2px #64b5f6", background: "#e3f2fd" } : {}),
      ...(empty && !flash && !active ? { borderColor: "#e57373" } : {}),
    };
  };

  return (
    <div style={{ maxWidth: 820, margin: "0 auto", padding: "16px 20px 40px" }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>{schema.name}</h2>
        {schema.description && (
          <p className="muted small" style={{ margin: "4px 0 0" }}>{schema.description}</p>
        )}
        <div className="muted small" style={{ marginTop: 8 }}>
          {filled} of {required} required fields filled
          {lastFilled && flashField && <span style={{ marginLeft: 12, color: "#2e7d32" }}>✨ just filled: {lastFilled}</span>}
        </div>
      </div>

      {schema.sections.map((section) => (
        <fieldset
          key={section.id}
          style={{
            marginBottom: 16,
            padding: "12px 16px",
            border: "1px solid var(--color-border, #e0e0e0)",
            borderRadius: 8,
            background: "#fff",
          }}
        >
          <legend style={{ padding: "0 8px", fontWeight: 600, fontSize: 14, color: "#555" }}>
            {section.label}
          </legend>
          {section.fields.map((f) => {
            const v = values[f.id];
            const id = `field-${f.id}`;
            const refProp = currentField === f.id ? { ref: currentRef } : {};
            return (
              <div key={f.id} style={{ marginBottom: 12 }} {...refProp}>
                <label htmlFor={id} style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
                  {f.label}
                  {f.required && <span style={{ color: "#c62828", marginLeft: 4 }}>*</span>}
                </label>
                {renderField(f, v, (val) => emit(f.id, val), fieldStyle(f), id)}
              </div>
            );
          })}
        </fieldset>
      ))}

      <div style={{ display: "flex", gap: 10, marginTop: 16, alignItems: "center" }}>
        <button
          className="btn primary"
          disabled={!canSubmit}
          onClick={() => onAction?.({ type: "submit_form_click" })}
          title={canSubmit ? "" : "Fill all required fields first"}
        >
          Submit referral
        </button>
        <button
          className="btn"
          onClick={() => onAction?.({ type: "clear_form_click" })}
        >
          Clear form
        </button>
        <span className="muted small" style={{ marginLeft: "auto" }}>
          {canSubmit ? "Ready to submit" : "Missing required fields"}
        </span>
      </div>
    </div>
  );
}

function renderField(f, value, onChange, style, id) {
  const common = {
    id,
    style: {
      width: "100%",
      padding: "6px 10px",
      fontSize: 14,
      border: "1px solid var(--color-border, #ccc)",
      borderRadius: 4,
      ...style,
    },
  };

  if (f.type === "textarea") {
    return (
      <textarea
        {...common}
        rows={f.rows || 2}
        value={value || ""}
        placeholder={f.placeholder || ""}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...common.style, resize: "vertical" }}
      />
    );
  }

  if (f.type === "date") {
    return (
      <input
        {...common}
        type="date"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (f.type === "email") {
    return (
      <input
        {...common}
        type="email"
        value={value || ""}
        placeholder={f.placeholder || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (f.type === "phone") {
    return (
      <input
        {...common}
        type="tel"
        value={value || ""}
        placeholder={f.placeholder || "+1..."}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (f.type === "enum") {
    return (
      <select
        {...common}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">—</option>
        {(f.options || []).map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    );
  }

  if (f.type === "boolean") {
    return (
      <div style={{ display: "flex", gap: 16, paddingTop: 4 }}>
        <label><input type="radio" checked={value === true}  onChange={() => onChange(true)} /> Yes</label>
        <label><input type="radio" checked={value === false} onChange={() => onChange(false)} /> No</label>
      </div>
    );
  }

  if (f.type === "list") {
    const items = Array.isArray(value) ? value : [];
    const set = (idx, val) => onChange(items.map((x, i) => (i === idx ? val : x)));
    const add = () => onChange([...items, ""]);
    const del = (idx) => onChange(items.filter((_, i) => i !== idx));
    return (
      <div style={style}>
        {items.map((item, i) => (
          <div key={i} style={{ display: "flex", gap: 6, marginBottom: 4 }}>
            <input
              type="text"
              value={item || ""}
              onChange={(e) => set(i, e.target.value)}
              style={{ flex: 1, padding: "6px 10px", fontSize: 14, border: "1px solid var(--color-border, #ccc)", borderRadius: 4 }}
            />
            <button
              onClick={() => del(i)}
              style={{ padding: "2px 8px", fontSize: 12, border: "1px solid #ccc", background: "#fafafa", cursor: "pointer", borderRadius: 4 }}
              title="Remove"
            >×</button>
          </div>
        ))}
        <button
          onClick={add}
          className="btn btn-sm"
          style={{ marginTop: 2 }}
        >+ add item</button>
      </div>
    );
  }

  // Default: text
  return (
    <input
      {...common}
      type="text"
      value={value || ""}
      placeholder={f.placeholder || ""}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}
